/**
 * Service worker registration with lightweight install/update flow.
 * Exposed events:
 *   - "ccm-install-available" (e.detail = BeforeInstallPromptEvent) -> show "Install" button
 *   - "ccm-update-available"  -> ask user to refresh; call promptUpdate() to apply
 */
let deferredPrompt = null;
let waitingWorker = null;

export function registerServiceWorker() {
  if (typeof window === "undefined") return;
  if (!("serviceWorker" in navigator)) return;

  // Capture install prompt (Chrome / Android / desktop PWA-capable browsers)
  window.addEventListener("beforeinstallprompt", (e) => {
    e.preventDefault();
    deferredPrompt = e;
    window.dispatchEvent(new CustomEvent("ccm-install-available"));
  });

  window.addEventListener("appinstalled", () => {
    deferredPrompt = null;
    window.dispatchEvent(new CustomEvent("ccm-installed"));
  });

  // Register after the page finished loading to avoid stealing bandwidth
  window.addEventListener("load", () => {
    navigator.serviceWorker
      .register("/service-worker.js")
      .then((reg) => {
        // Track future updates
        reg.addEventListener("updatefound", () => {
          const newSW = reg.installing;
          if (!newSW) return;
          newSW.addEventListener("statechange", () => {
            if (
              newSW.state === "installed" &&
              navigator.serviceWorker.controller
            ) {
              // A new version is waiting
              waitingWorker = newSW;
              window.dispatchEvent(new CustomEvent("ccm-update-available"));
            }
          });
        });
      })
      .catch((err) => {
        console.warn("[PWA] service worker registration failed", err);
      });

    // When the active worker changes (after we tell it to skip waiting), reload once
    let reloaded = false;
    navigator.serviceWorker.addEventListener("controllerchange", () => {
      if (reloaded) return;
      reloaded = true;
      window.location.reload();
    });
  });
}

export async function promptInstall() {
  if (!deferredPrompt) return { outcome: "unavailable" };
  deferredPrompt.prompt();
  const { outcome } = await deferredPrompt.userChoice;
  deferredPrompt = null;
  return { outcome };
}

export function isInstallAvailable() {
  return !!deferredPrompt;
}

export function isStandalone() {
  if (typeof window === "undefined") return false;
  return (
    window.matchMedia("(display-mode: standalone)").matches ||
    // iOS Safari adds this once installed
    window.navigator.standalone === true
  );
}

export function isIOS() {
  if (typeof navigator === "undefined") return false;
  const ua = navigator.userAgent || "";
  // iPad on iOS 13+ pretends to be Mac; check touchpoints
  return (
    /iPhone|iPad|iPod/i.test(ua) ||
    (ua.includes("Mac") && navigator.maxTouchPoints > 1)
  );
}

export function promptUpdate() {
  if (waitingWorker) {
    waitingWorker.postMessage("SKIP_WAITING");
  }
}
